import { View } from "react-native";
import React, { useState, useEffect } from "react";
import { useLocalSearchParams, useRouter, useNavigation } from "expo-router";
import { useAuth } from "@components/auth/useAuth";

import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMyGroups, editGroup, deleteGroup } from "@/services/firebase/group-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { GroupModel, Contact, Share } from "common247";
import { getMyContacts } from "@services/firebase/user-functions";
import { GroupEditor } from "@/components/groups/GroupEditor";
import { ThemedKebabMenu, IKebabActionItem } from "@/components/ThemedKebabMenu";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { navigateBackOrToList } from "@/utils/navigation";
import { entitySync } from "@/services/sync/entity-sync";

export default function EditGroupScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const services = useFirebase();
  const { user } = useAuth(true);
  const router = useRouter();
  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [group, setGroup] = useState<GroupModel | null>(null);
  const [contactsList, setContactsList] = useState<Contact[]>([]);
  const [allGroups, setAllGroups] = useState<GroupModel[]>([]);
  const [shares, setShares] = useState<Share[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);

  const fetchData = async () => {
    if (!services || !id) return;
    setLoading(true);
    try {
      const [groupsRes, contactsRes] = await Promise.all([getMyGroups(services, {}), getMyContacts(services, { includeInactive: false })]);

      if (groupsRes.status) {
        const found = groupsRes.groups?.find((g) => g.id === id);
        if (found) {
          setGroup(found);
          setAllGroups(groupsRes.groups || []);
          // Fetch shares if it's the owner or editor
          if (found.userRole === "owner" || found.userRole === "editor") {
            fetchShares();
          }
        } else {
          CustomAlert.alert("Error", "Group not found.");
          navigateBackOrToList(router, "/(tabs)/my-groups", navigation);
        }
      }

      if (contactsRes.status) {
        setContactsList(contactsRes.contacts || []);
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
      CustomAlert.alert("Error", "Failed to load group data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchShares = async () => {
    if (!services || !id) return;
    setIsLoadingShares(true);
    try {
      const res = await getSharesForSource(services, { sourceId: id, sourceType: "group" });
      if (res.status) {
        setShares(res.shares);
      }
    } catch (error) {
      console.error("Error fetching shares:", error);
    } finally {
      setIsLoadingShares(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [services, id]);

  const handleSave = async (formData: any) => {
    if (!services || !group) return;

    setIsSaving(true);
    try {
      const res = await editGroup(services, {
        groupId: group.id!,
        ...formData,
      });

      if (res.status) {
        const updatedGroup: GroupModel = {
          ...(group || ({} as GroupModel)),
          ...formData,
          id: group.id!,
        };
        // Auto-save updates in place — do not close the edited item
        setGroup((prev) => (prev ? { ...prev, ...formData } : updatedGroup));
        entitySync.emit({
          type: "group",
          action: "update",
          id: group.id!,
          data: updatedGroup,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to update group.");
      }
    } catch (error) {
      console.error("Error saving group:", error);
      CustomAlert.alert("Error", "Unexpected error while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleShare = async (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    if (!services || !id) return;
    try {
      const res = await share(services, {
        sourceId: id,
        sourceType: "group",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        fetchShares();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to share.");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        fetchShares();
      }
    } catch (error) {
      console.error("Error unsharing:", error);
    }
  };

  const handleDelete = async () => {
    if (!services || !id) return;
    await confirmEntityDeletion({
      services,
      entityType: "group",
      entityId: id,
      entityName: group?.name,
      onProceedDelete: async (cascadeUnlink) => {
        setIsDeleting(true);
        try {
          const res = await deleteGroup(services, { groupId: id, cascadeUnlink });
          if (!res.status) {
            setIsDeleting(false);
          }
          return res;
        } catch (error) {
          setIsDeleting(false);
          throw error;
        }
      },
      onSuccess: () => {
        navigateBackOrToList(router, "/(tabs)/my-groups", navigation);
      },
      onError: () => {
        setIsDeleting(false);
      },
    });
  };

  const userRole = group?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";
  const isViewer = userRole === "viewer";

  const kebabActions: IKebabActionItem[] = [];
  if (group && canEdit) {
    kebabActions.push({
      id: "delete",
      label: "Delete Group",
      icon: "trash-outline",
      destructive: true,
      onPress: handleDelete,
      disabled: isDeleting,
    });
  }

  return (
    <ThemedScreen
      title={group ? `${isViewer ? "View" : "Edit"}: ${group.name}` : "Group Details"}
      loading={loading}
      rightComponent={kebabActions.length > 0 ? <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Group options" /> : undefined}
    >
      <View style={{ flex: 1 }}>
        <ThemedProgressBar loading={loading} />
        {!loading && group && (
          <GroupEditor
            group={group}
            contactsList={contactsList}
            allGroups={allGroups}
            shares={shares}
            isLoadingShares={isLoadingShares}
            currentUserId={user?.uid}
            onSave={handleSave}
            onShare={handleShare}
            onUnshare={handleUnshare}
            onCancel={() => navigateBackOrToList(router, "/(tabs)/my-groups", navigation)}
            isSaving={isSaving}
          />
        )}
      </View>
    </ThemedScreen>
  );
}
